import React, { useState, useEffect, useMemo } from "react";
import { db, collection, getDocs } from "../firebase";
import { useTeam } from "../contexts/TeamContext";
import { useTeamPath } from "../hooks/useTeamPath";
import { useHistory } from "react-router-dom";
import { useSeo } from "../hooks/useSeo";
import Leaderboard from "./Leaderboard";
import "../App.css";

/* eslint-disable jsx-a11y/accessible-emoji */

const looksLikeEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || "").trim());

const Home = () => {
  const { teamId, teamData, slug, loading: teamLoading } = useTeam();
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [members, setMembers] = useState([]);
  const [archivedSeasons, setArchivedSeasons] = useState([]); // [{ id, number }]
  const [selectedSeasonId, setSelectedSeasonId] = useState(null);
  const [archivePlayers, setArchivePlayers] = useState([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [randomGif, setRandomGif] = useState("");
  const [showArchive, setShowArchive] = useState(false);

  const history = useHistory();
  const tp = useTeamPath();

  const publicTeamUrl = slug ? `https://statarena.app/t/${slug}` : "";
  const teamName = teamData?.name || "StatArena tim";
  const teamDescription =
    teamData?.description ||
    `${teamName} na StatAreni: raspored utakmica, igrači i nogometna statistika na jednom mjestu.`;

  // SEO robots logika:
  // - Dok teamData učitava, NE diramo meta tag (inače Googlebot može pokupiti privremeni noindex iz statičkog HTML-a u SPA renderu).
  // - Default je "index, follow" — noindex se postavlja samo za eksplicitno privatne timove.
  //   (Privatnost podataka se rješava na razini API/Firestore pravila, ne preko noindex-a.)
  const robotsDirective = teamLoading
    ? undefined
    : teamData?.visibility === "private"
      ? "noindex, nofollow"
      : "index, follow, max-image-preview:large";

  useSeo({
    title: slug ? `${teamName} | StatArena tim` : "StatArena tim",
    description: teamDescription,
    canonical: slug ? publicTeamUrl : undefined,
    robots: robotsDirective,
    ogUrl: slug && teamData?.visibility !== "private" ? publicTeamUrl : undefined,
    ogTitle: slug ? `${teamName} | StatArena` : undefined,
    ogDescription: teamDescription,
  });

  useEffect(() => {
    if (!teamId) return;

    const fetchMatches = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "teams", teamId, "matches"));
        const matchList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        matchList.sort((a, b) => new Date(b.date) - new Date(a.date));
        setMatches(matchList);
      } catch (error) {
        console.error("Greška pri dohvaćanju utakmica:", error);
      }
    };

    const fetchPlayers = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "teams", teamId, "players"));
        const playersList = querySnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((player) => !player.migratedToMemberId);
        setPlayers(playersList);
      } catch (error) {
        console.error("Greška pri dohvaćanju igrača:", error);
      }
    };

    const fetchMembers = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "teams", teamId, "members"));
        const membersList = querySnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((member) => !member.removed);
        setMembers(membersList);
      } catch (error) {
        console.error("Greška pri dohvaćanju članova:", error);
      }
    };

    const fetchArchivedSeasons = async () => {
      try {
        const seasonsSnap = await getDocs(collection(db, "teams", teamId, "seasons"));
        const list = seasonsSnap.docs
          .map((d) => {
            const fromField = Number(d.data()?.number);
            const fromId = parseInt(d.id, 10);
            const number = Number.isFinite(fromField) ? fromField : (Number.isFinite(fromId) ? fromId : null);
            return { id: d.id, number };
          })
          .filter((s) => s.number !== null)
          .sort((a, b) => a.number - b.number);
        setArchivedSeasons(list);
        // Defaultno odaberi posljednju arhiviranu sezonu.
        if (list.length > 0) {
          setSelectedSeasonId((prev) => prev ?? list[list.length - 1].id);
        }
      } catch (error) {
        console.error("Greška pri dohvaćanju arhive sezona:", error);
      }
    };

    fetchMatches();
    fetchPlayers();
    fetchMembers();
    fetchArchivedSeasons();
  }, [teamId]);

  // Učitaj igrače odabrane arhivirane sezone kad se promijeni odabir.
  useEffect(() => {
    if (!teamId || !selectedSeasonId) {
      setArchivePlayers([]);
      return;
    }
    let cancelled = false;
    setArchiveLoading(true);
    (async () => {
      try {
        const snap = await getDocs(
          collection(db, "teams", teamId, "seasons", selectedSeasonId, "players")
        );
        if (cancelled) return;
        setArchivePlayers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error("Greška pri dohvaćanju igrača iz arhive:", error);
        if (!cancelled) setArchivePlayers([]);
      } finally {
        if (!cancelled) setArchiveLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId, selectedSeasonId]);

  const roster = useMemo(() => {
    const byName = new Map();
    players.forEach((player) => {
      if (looksLikeEmail(player.name)) return;
      byName.set((player.name || "").trim().toLowerCase(), {
        id: player.id,
        name: player.name || "(bez imena)",
        goals: player.goals || 0,
        assists: player.assists || 0,
        matchesPlayed: player.matchesPlayed || 0,
      });
    });
    members.forEach((member) => {
      if (member.role === "owner") return;
      const key = (member.displayName || "").trim().toLowerCase();
      byName.set(key, {
        id: member.id,
        name: member.displayName || member.email || "(bez imena)",
        goals: member.goals || byName.get(key)?.goals || 0,
        assists: member.assists || byName.get(key)?.assists || 0,
        matchesPlayed: member.matchesPlayed || byName.get(key)?.matchesPlayed || 0,
      });
    });
    return Array.from(byName.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [players, members]);

  const getRandomFootballMeme = () => {
    const memes = [
      "https://media1.giphy.com/media/kxUhZ0TY46X1Dk48ru/giphy.gif",
      "https://media4.giphy.com/media/i8Udr19doZnqEQTPxu/giphy.gif",
      "https://media0.giphy.com/media/8TY5Uw0CNztcwYuP1t/giphy.gif",
      "https://media0.giphy.com/media/dyuc5DfSUg1RGg8P3p/giphy.gif",
    ];
    return memes[Math.floor(Math.random() * memes.length)];
  };

  const handlePlayerClick = (player) => {
    setSelectedPlayer(player);
    setRandomGif(getRandomFootballMeme());
  };

  const initials = (name) => {
    if (!name) return "?";
    return name
      .trim()
      .split(/\s+/)
      .map((word) => word[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  const { nextMatch } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const upcoming = matches
      .filter((match) => match.date && new Date(match.date) >= today)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    return { nextMatch: upcoming[0] || null };
  }, [matches]);

  const isPast = (match) => match && match.date && new Date(match.date) < new Date(new Date().setHours(0, 0, 0, 0));
  const hasScore = (match) => match && (match.scoreA !== undefined || match.scoreB !== undefined);

  if (teamLoading) {
    return <div className="loading-screen">Učitavanje tima…</div>;
  }

  return (
    <div>
      <section className="hero">
        <span className="hero-eyebrow">StatArena</span>
        <h1 className="hero-title">Dobrodošao natrag. Spreman za novi gol?</h1>
        <p className="hero-subtitle">
          Prati termine, statistiku i MVP rang-listu. Analiziraj utakmice, usporedi igrače i planiraj nove sastave - sve na jednom mjestu.
        </p>
        <div className="hero-actions">
          {nextMatch && (
            <button className="btn btn-primary" onClick={() => history.push(tp(`/match/${nextMatch.id}`))}>
              Sljedeća utakmica: {nextMatch.date}
            </button>
          )}
        </div>
      </section>

      <div className="section-header">
        <h2>Nadolazeće i odigrane utakmice</h2>
        <span className="section-badge">{matches.length} ukupno</span>
      </div>

      <div className="match-grid">
        {matches.length > 0 ? (
          matches.map((match) => {
            const past = isPast(match);
            const withScore = hasScore(match);
            const scoreA = Number(match.scoreA) || 0;
            const scoreB = Number(match.scoreB) || 0;
            let scoreClass = "";
            if (withScore && past) scoreClass = scoreA > scoreB ? "win-left" : scoreB > scoreA ? "win-right" : "";

            return (
              <article key={match.id} className="match-card" onClick={() => history.push(tp(`/match/${match.id}`))}>
                <div className="match-meta">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span className="match-date">
                      {match.date} · {match.time || "—"}
                    </span>
                    <span className={`match-status ${past && withScore ? "status-final" : "status-upcoming"}`} style={{ position: 'static', marginLeft: 'auto' }}>
                      {past && withScore ? "Završeno" : "Uskoro"}
                    </span>
                  </div>
                  {match.location && <span className="match-venue">📍 {match.location}</span>}
                </div>
                <div className="match-teams">
                  <div className="match-team">
                    <span className="team-label">Tim A</span>
                    {match.teamA?.length
                      ? match.teamA.map((p, i) => <span key={i} className="team-players">{p}</span>)
                      : <span className="team-players">Još nije određen</span>}
                  </div>
                  <div className={`match-score ${scoreClass}`}>
                    <span className="score-a">{match.scoreA ?? "–"}</span>
                    <span className="divider">:</span>
                    <span className="score-b">{match.scoreB ?? "–"}</span>
                  </div>
                  <div className="match-team team-right">
                    <span className="team-label">Tim B</span>
                    {match.teamB?.length
                      ? match.teamB.map((p, i) => <span key={i} className="team-players">{p}</span>)
                      : <span className="team-players">Još nije određen</span>}
                  </div>
                </div>
              </article>
            );
          })
        ) : (
          <div className="empty-state">Nema dostupnih utakmica.</div>
        )}
      </div>

      <Leaderboard />

      <div className="section-header">
        <h2>Lista igrača</h2>
        <span className="section-badge">{roster.length} aktivnih</span>
      </div>
      <div className="player-grid">
        {roster.length > 0 ? (
          roster.map((player) => (
            <div
              key={player.id}
              className={`player-card ${selectedPlayer?.id === player.id ? "selected" : ""}`}
              onClick={() => handlePlayerClick(player)}
            >
              <div className="player-avatar">{initials(player.name)}</div>
              <span className="player-name">{player.name}</span>
            </div>
          ))
        ) : (
          <div className="empty-state">Nema dostupnih igrača.</div>
        )}
      </div>

      {selectedPlayer && (
        <div style={{ marginTop: 24 }}>
          <div className="section-header">
            <h2>{selectedPlayer.name} · Statistika</h2>
          </div>
          <div className="stats-grid">
            <div className="stat-card">
              <span role="img" aria-label="goal">⚽</span>
              <h3>{selectedPlayer.goals || 0}</h3>
              <p>Golovi</p>
            </div>
            <div className="stat-card">
              <span role="img" aria-label="assist">🎯</span>
              <h3>{selectedPlayer.assists || 0}</h3>
              <p>Asistencije</p>
            </div>
            <div className="stat-card">
              <span role="img" aria-label="games">🏆</span>
              <h3>{selectedPlayer.matchesPlayed || 0}</h3>
              <p>Odigrane utakmice</p>
            </div>
          </div>
          <div className="stats-grid">
            <div className="stat-card">
              <span role="img" aria-label="avg-goals">📊</span>
              <h3>{((selectedPlayer.goals || 0) / (selectedPlayer.matchesPlayed || 1)).toFixed(2)}</h3>
              <p>Golovi / utakmica</p>
            </div>
            <div className="stat-card">
              <span role="img" aria-label="avg-assists">📊</span>
              <h3>{((selectedPlayer.assists || 0) / (selectedPlayer.matchesPlayed || 1)).toFixed(2)}</h3>
              <p>Asistencije / utakmica</p>
            </div>
          </div>
          {randomGif && (
            <div className="meme-container">
              <img src={randomGif} alt="Football Meme" className="meme-image" />
            </div>
          )}
        </div>
      )}

      <div id="archive" style={{ marginTop: 60 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: showArchive ? 20 : 0,
          }}
        >
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowArchive((prev) => !prev)}
            aria-expanded={showArchive}
            aria-controls="archive-content"
            disabled={archivedSeasons.length === 0}
            style={{ opacity: 0.75, fontSize: "0.9em" }}
          >
            {archivedSeasons.length === 0
              ? "Nema arhiviranih sezona"
              : showArchive
                ? "Sakrij arhivu sezona"
                : `Prikaži arhivu sezona (${archivedSeasons.length})`}
          </button>
        </div>

        {showArchive && archivedSeasons.length > 0 && (
          <div id="archive-content">
            <div
              className="section-header"
              style={{ flexWrap: "wrap", gap: 12, alignItems: "center" }}
            >
              <h2 style={{ margin: 0 }}>
                Arhiva · Sezona{" "}
                {archivedSeasons.find((s) => s.id === selectedSeasonId)?.number ?? "—"}
              </h2>
              <span className="section-badge">{archivePlayers.length} igrača</span>
              {archivedSeasons.length > 1 && (
                <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
                  <span style={{ color: "var(--text-3)", fontSize: "0.85rem" }}>Sezona:</span>
                  <select
                    className="input-field"
                    value={selectedSeasonId || ""}
                    onChange={(e) => setSelectedSeasonId(e.target.value)}
                    style={{ minWidth: 110 }}
                  >
                    {archivedSeasons.map((s) => (
                      <option key={s.id} value={s.id}>
                        Sezona {s.number}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            <div className="scroll-container">
              <table className="leaderboard-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Igrač</th>
                    <th>Golovi</th>
                    <th>Asist.</th>
                    <th>U × B</th>
                    <th>Uspješ.</th>
                    <th>Bodovi</th>
                    <th>GR</th>
                    <th>Post.</th>
                    <th>Prim.</th>
                    <th>W</th>
                    <th>D</th>
                    <th>L</th>
                    <th>P</th>
                  </tr>
                </thead>
                <tbody>
                  {archiveLoading ? (
                    <tr>
                      <td colSpan="14">Učitavanje arhive…</td>
                    </tr>
                  ) : archivePlayers.length > 0 ? (
                    archivePlayers
                      .slice()
                      .sort((a, b) => (b.points || 0) - (a.points || 0))
                      .map((player, index) => (
                        <tr key={player.id}>
                          <td>{index + 1}</td>
                          <td>{player.name}</td>
                          <td>{player.goals || 0}</td>
                          <td>{player.assists || 0}</td>
                          <td>{player.successPoints || 0}</td>
                          <td>{player.successRate || "0%"}</td>
                          <td>{player.points || 0}</td>
                          <td>{player.goalDifference || 0}</td>
                          <td>{player.goalsFor || 0}</td>
                          <td>{player.goalsAgainst || 0}</td>
                          <td>{player.wins || 0}</td>
                          <td>{player.draws || 0}</td>
                          <td>{player.losses || 0}</td>
                          <td>{player.matchesPlayed || 0}</td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan="14">Nema arhiviranih podataka.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* eslint-enable jsx-a11y/accessible-emoji */

export default Home;
